import React, {
  useMemo, useEffect, useState, useCallback,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

// Hooks
import useAuth from 'hooks/useAuth';

// Components
import Text from 'components/Text';
import ButtonLink from 'components/ButtonLink';
import Icon from 'components/Icon';
import { ButtonTypes } from 'components/Button';

// Modals
import SelectSchoolDashboardModal from 'modals/SelectSchoolDashboardModal';

// Hooks
import { useModal } from 'hooks/useModal';
import { useResponsive } from 'hooks/useResponsive';

// Queries
import { getSchoolById } from 'data/school';

// Utils
import { getSchoolLogo } from 'utils/fileHelper';

// Theme
import { colors } from '../../theme/theme';

// img
// import logo from '../../images/SIS_logo.svg';
import logo from '../../images/AHP_logo.png';
import background from '../../images/background.svg';

// style
import './Header.css';
import {
  DashboardButton,
  StyledSchoolLogo,
  Tab,
  OptionsMenuCard,
  OptionsMenuRow,
  OptionsMenuCol,
} from './style';

const Container = styled.div`
  background-image: url(${background});
  width: 100%;
  padding: 10px 0;
  background-position: center;
`;

const Header = React.memo(() => {
  // Hooks
  const { t } = useTranslation();
  const { signOut, isUserType, user } = useAuth();
  const history = useHistory();
  const { state: stateLocation } = useLocation<{ school: { value: string } }>();
  const { isOpen, toggleModal, openModal } = useModal();
  const { isMobile } = useResponsive();

  // States
  const [currentPage, setCurrentPage] = useState('');
  const [open, setOpen] = React.useState(false);
  const [schoolId, setSchoolId] = React.useState('');

  // Memos
  const imageSize = useMemo(() => {
    if (isMobile) {
      return {
        sisLogo: {
          height: 50,
          width: 96,
        },
      };
    }
    return {
      sisLogo: {
        height: 100,
        width: 192,
      },
    };
  }, [isMobile]);

  const isAdmin = useMemo(() => !!isUserType('admin'), [isUserType]);
  const isDashboard = useMemo(() => currentPage === 'dashboard', [currentPage]);

  // Queries
  const [getSchool, { data: dataGetSchoolById }] = useLazyQuery<
  GetSchoolByIdResponse,
  GetSchoolByIdVariables
  >(getSchoolById, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const initialRoot = history.location.pathname.split('/')[1];
    setCurrentPage(initialRoot || '');
    history.listen(location => {
      const splittedPath = location.pathname.split('/');
      const updatedRoot = splittedPath[1];
      setCurrentPage(updatedRoot || '');
      // check that is only the dashboar page and not a nested page
      if (updatedRoot === 'dashboard' && splittedPath.length === 2) {
        const { state }: any = location;
        setSchoolId(isAdmin ? state?.school.value : user?.school_id);
      }
    });

    setSchoolId(isAdmin ? stateLocation?.school?.value : user?.school_id || '');
  }, [user]);

  useEffect(() => {
    getSchool({ variables: { schoolId } });
  }, [schoolId]);

  const handleSelectSchool = useCallback(() => {
    openModal();
    if (isMobile) {
      setOpen(o => !o);
    }
  }, [openModal]);

  return (
    <>
      <Container>
        <div className={`site-container d-flex ${!isAdmin && isMobile ? 'justify-content-end' : ''}`}>
          {isAdmin && isMobile && (
            <div className={`col-${isMobile ? 4 : 3} my-auto`}>
              <ButtonLink className="ml-3" onClick={() => setOpen(o => !o)}>
                <div className="d-flex cursor-pointer">
                  <Icon icon={open ? 'las la-times' : 'las la-bars'} size={20} color={colors.white} />
                </div>
              </ButtonLink>
            </div>
          )}
          {open && (
            <OptionsMenuCard>
              <OptionsMenuRow className="row mb-3">
                <OptionsMenuCol
                  className="col"
                  onClick={() => {
                    history.push('/claims');
                    setOpen(o => !o);
                  }}
                >
                  <Text
                    size={14}
                    fontFamily={currentPage === 'claims' ? 'BOLD' : 'Regular'}
                    color={currentPage === 'claims' ? colors.white : colors.white500}
                  >
                    {t('HEADER.CLAIMS')}
                  </Text>
                </OptionsMenuCol>
              </OptionsMenuRow>
              <OptionsMenuRow className="row mb-3">
                <OptionsMenuCol
                  className="col"
                  onClick={() => {
                    history.push('/tpas');
                    setOpen(o => !o);
                  }}
                >
                  <Text
                    size={14}
                    fontFamily={currentPage === 'tpas' ? 'BOLD' : 'Regular'}
                    color={currentPage === 'tpas' ? colors.white : colors.white500}
                  >
                    {t('HEADER.TPA_ADMIN')}
                  </Text>
                </OptionsMenuCol>
              </OptionsMenuRow>
              <OptionsMenuRow className="row mb-3">
                <OptionsMenuCol
                  className="col"
                  onClick={() => {
                    history.push('/schools');
                    setOpen(o => !o);
                  }}
                >
                  <Text
                    size={14}
                    fontFamily={currentPage === 'schools' ? 'BOLD' : 'Regular'}
                    color={currentPage === 'schools' ? colors.white : colors.white500}
                  >
                    {t('HEADER.SCHOOLS')}
                  </Text>
                </OptionsMenuCol>
              </OptionsMenuRow>
              <OptionsMenuRow className="row mb-3">
                <OptionsMenuCol
                  className="col"
                  onClick={() => {
                    history.push('/sales-reps');
                    setOpen(o => !o);
                  }}
                >
                  <Text
                    size={14}
                    fontFamily={currentPage === 'sales-reps' ? 'BOLD' : 'Regular'}
                    color={currentPage === 'sales-reps' ? colors.white : colors.white500}
                  >
                    {t('HEADER.SALES_REP')}
                  </Text>
                </OptionsMenuCol>
              </OptionsMenuRow>
              <OptionsMenuRow className="row mb-3">
                <OptionsMenuCol
                  className="col"
                  onClick={() => {
                    history.push('/sales-reps');
                    setOpen(o => !o);
                  }}
                >
                  <Text
                    size={14}
                    fontFamily={currentPage === 'activities' ? 'BOLD' : 'Regular'}
                    color={currentPage === 'activities' ? colors.white : colors.white500}
                  >
                    {t('HEADER.ACTIVITIES')}
                  </Text>
                </OptionsMenuCol>
              </OptionsMenuRow>
              <OptionsMenuRow className="row">
                <OptionsMenuCol className="col">
                  <DashboardButton onClick={handleSelectSchool} type={ButtonTypes.OUTLINE}>
                    <Icon icon="las la-exchange-alt" color={colors.white} />
                    {' '}
                    {t('HEADER.SCHOOL_DASHBOARD')}
                  </DashboardButton>
                </OptionsMenuCol>
              </OptionsMenuRow>
            </OptionsMenuCard>
          )}
          <div className={`col-${isMobile ? 4 : 3} ${isMobile ? 'd-flex justify-content-center' : ''}`}>
            <Link to="/">
              <img
                src={logo}
                alt="sis-logo"
                width={imageSize.sisLogo.width}
                height={imageSize.sisLogo.height}
              />
            </Link>
          </div>
          {isAdmin && !isMobile && (
            <div
              className={`col-7 d-flex ${
                isDashboard ? 'flex-column justify-content-between align-items-center' : 'align-items-end'
              } `}
            >
              {isDashboard && (
                <div>
                  <Text size={32} color={colors.white} lineHeight="1.31" weight="500" fontFamily="Medium">
                    {dataGetSchoolById?.schools_by_pk ? dataGetSchoolById.schools_by_pk.name : ''}
                  </Text>
                </div>
              )}
              <div className="d-flex">
                <Tab active={currentPage === 'claims'} onClick={() => history.push('/claims')}>
                  {t('HEADER.CLAIMS')}
                </Tab>
                <Tab active={currentPage === 'tpas'} onClick={() => history.push('/tpas')}>
                  {t('HEADER.TPA_ADMIN')}
                </Tab>
                <Tab active={currentPage === 'schools'} onClick={() => history.push('/schools')}>
                  {t('HEADER.SCHOOLS')}
                </Tab>
                <Tab active={currentPage === 'sales-reps'} onClick={() => history.push('/sales-reps')}>
                  {t('HEADER.SALES_REP')}
                </Tab>
                <Tab active={currentPage === 'activities'} onClick={() => history.push('/activities')}>
                  {t('HEADER.ACTIVITIES')}
                </Tab>
              </div>
            </div>
          )}
          {!isAdmin && !isMobile && (
            <div className={`col-${isMobile ? 5 : 7} text-center d-flex flex-column justify-content-center`}>
              {!isMobile && (
                <Text size={32} color={colors.white} lineHeight="1.31" weight="500" fontFamily="Medium">
                  {dataGetSchoolById?.schools_by_pk ? dataGetSchoolById.schools_by_pk.name : ''}
                </Text>
              )}
              <Text size={isMobile ? 14 : 20} color={colors.white} lineHeight="1.6">
                {t('SERVICE_CENTER')}
              </Text>
            </div>
          )}

          <div
            className={`col-${isMobile ? 4 : 2} text-right d-flex justify-content-end`}
            style={{
              flexDirection: 'column',
              padding: 0,
              alignItems: 'flex-end',
              marginRight: 4,
            }}
          >
            {(!isAdmin || (isAdmin && isDashboard)) && (
              <StyledSchoolLogo
                src={getSchoolLogo(dataGetSchoolById?.schools_by_pk)}
                $smallFormat={isMobile || isAdmin}
              />
            )}
            {isAdmin && !isMobile && (
              <DashboardButton onClick={handleSelectSchool} type={ButtonTypes.OUTLINE}>
                <Icon icon="las la-exchange-alt" color={colors.white} />
                {' '}
                {t('HEADER.SCHOOL_DASHBOARD')}
              </DashboardButton>
            )}
          </div>
          <div className={`log-out ${isMobile ? 'px-1' : ''}`}>
            <ButtonLink onClick={signOut}>
              <div className="d-flex cursor-pointer">
                <Icon icon="las la-power-off" size={16} color={colors.grey} />
                {!isMobile && (
                  <Text size={14} color={colors.grey} className="ml-2">
                    {t('LOG_OUT')}
                  </Text>
                )}
              </div>
            </ButtonLink>
          </div>
        </div>
      </Container>

      {/* Modals */}
      <SelectSchoolDashboardModal isOpen={isOpen} toggle={toggleModal} />
    </>
  );
});

export default Header;
